// components/tier-branch/TierManagement.tsx
// Tier Overview page: lists configured tiers, with "Tier Wise Manage User"
// (auto-assign) and "Add New Tier" actions. Top-level route /tier-management.
import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, Stack,
} from "@mui/material";
import { Add, GroupWork } from "@mui/icons-material";
import * as api from "../../services/tierBranchApi";
import type { Tier, Branch } from "../../types/tierBranch";
import { usePermissions } from "../../hooks/usePermissions";
import { useSnack } from "./useSnack";
import TierCard from "./TierCard";
import CreateEditTierModal from "./CreateEditTierModal";
import AutoAssignDialog from "./AutoAssignDialog";
import TierUsersDialog from "./TierUsersDialog";
import AddBranchToTierDialog from "./AddBranchToTierDialog";

const TierManagement: React.FC = () => {
  const { can } = usePermissions();
  const canManage = can("canManageTiers");
  const canAutoAssign = can("canRunTierAutoAssign");
  const { notify, SnackBar } = useSnack();

  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Tier | null>(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [usersTier, setUsersTier] = useState<Tier | null>(null);
  const [addBranchTier, setAddBranchTier] = useState<Tier | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listTiers();
      setTiers(Array.isArray(data) ? data : []);
    } catch (e: any) {
      notify(e?.message || "Failed to load tiers", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };
  const openEdit = (t: Tier) => {
    setEditing(t);
    setEditorOpen(true);
  };

  const handleToggle = async (
    t: Tier,
    patch: { isActive?: boolean; isManual?: boolean; manualPaymentEnabled?: boolean }
  ) => {
    // Optimistic update, revert on failure.
    setTiers((prev) => prev.map((x) => (x.id === t.id ? { ...x, ...patch } : x)));
    try {
      await api.updateTierStatus(t.id, patch);
    } catch (e: any) {
      notify(e?.message || "Failed to update tier", "error");
      load();
    }
  };

  const handleToggleBranch = async (b: Branch) => {
    try {
      await api.updateBranchStatus(b.id, { enabled: !b.enabled });
      load();
    } catch (e: any) {
      notify(e?.message || "Failed to update branch", "error");
    }
  };

  const handleDeleteBranch = async (b: Branch) => {
    if (!window.confirm(`Delete branch "${b.name}"?`)) return;
    try {
      await api.deleteBranch(b.id);
      notify("Branch deleted", "success");
      load();
    } catch (e: any) {
      notify(e?.message || "Failed to delete branch", "error");
    }
  };

  const handleDelete = async (t: Tier) => {
    if (!window.confirm(`Delete tier "${t.name}"? Users keep their tier_id until reassigned.`))
      return;
    try {
      await api.deleteTier(t.id);
      notify("Tier deleted", "success");
      load();
    } catch (e: any) {
      notify(e?.message || "Failed to delete tier", "error");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Tier Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage transaction tiers and their limits
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {canAutoAssign && (
            <Button
              variant="contained"
              color="success"
              startIcon={<GroupWork />}
              onClick={() => setAutoOpen(true)}
            >
              Tier Wise Manage User
            </Button>
          )}
          {canManage && (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Add New Tier
            </Button>
          )}
        </Stack>
      </Box>

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
        Tier Overview
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {tiers.length} tier{tiers.length === 1 ? "" : "s"} configured
        </Typography>
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : tiers.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4 }}>
          No tiers configured yet. Click “Add New Tier” to create one.
        </Typography>
      ) : (
        tiers.map((t) => (
          <TierCard
            key={t.id}
            tier={t}
            canManage={canManage}
            onEdit={openEdit}
            onDelete={handleDelete}
            onViewUsers={setUsersTier}
            onToggle={handleToggle}
            onAddBranch={setAddBranchTier}
            onToggleBranch={handleToggleBranch}
            onDeleteBranch={handleDeleteBranch}
          />
        ))
      )}

      <CreateEditTierModal
        open={editorOpen}
        tier={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={load}
        notify={notify}
      />
      <AutoAssignDialog
        open={autoOpen}
        onClose={() => setAutoOpen(false)}
        onCommitted={load}
        notify={notify}
      />
      <TierUsersDialog
        open={!!usersTier}
        tier={usersTier}
        onClose={() => setUsersTier(null)}
        notify={notify}
      />
      <AddBranchToTierDialog
        open={!!addBranchTier}
        tier={addBranchTier}
        onClose={() => setAddBranchTier(null)}
        onSaved={load}
        notify={notify}
      />

      {SnackBar}
    </Box>
  );
};

export default TierManagement;
